// Funnel analytics — a thin wrapper over Umami.
//
// WHY THIS EXISTS
// Umami records page views only, so we can see that someone landed on the
// sitter wizard but not which of its seven steps made them give up. These
// events fill that gap. Nothing else changes: still no cookies, still no
// consent banner.
//
// WHERE TO SEE THEM
// Umami Cloud → LiluPets → "Events". Click an event name to break it down by
// its properties (e.g. which wizard step).
//
// THE EVENTS
//   registration_submitted     SERVER-CONFIRMED: the account exists and a
//                              role was chosen. `role` owner | sitter | both,
//                              `method` email | google.
//   sitter_wizard_completed    SERVER-CONFIRMED: the wizard's last step
//                              created the sitter profile.
//   care_request_sent          SERVER-CONFIRMED: an owner's request was
//                              saved. `city` (town only), `service_type`,
//                              `pet_count`.
//   subscription_started       SERVER-CONFIRMED: Stripe brought a sitter back
//                              to /subscription/success. `plan` monthly |
//                              yearly | unknown.
//   (Server-confirmed events come from AnalyticsEvents via the page prop
//   `analytics_events`; every other event fires in the browser.)
//   share_card_downloaded      A sitter downloaded a share card on the Share Kit
//                              page. `variant` is story or post.
//   share_card_native_shared   A sitter sent a card through the phone's share
//                              sheet (Instagram, WhatsApp, Telegram…).
//   share_card_caption_copied  A sitter copied a ready caption; `channel` is
//                              social, messenger or linkedin.
//   sitter_wizard_step         A sitter opened one step of the 7-step sitter
//                              signup. Property `step` says which one
//                              (basic_info → contact_info → services →
//                              availability → about_you → identity → pricing)
//                              and `step_number` is its position. Compare the
//                              counts to see where people stop.
//   owner_wizard_step          Same, for the 2-step owner signup
//                              (owner_details → pets).
//   owner_wizard_completed     An owner finished the owner signup.
//   home_search_submitted      Someone pressed Search in the homepage hero.
//                              Properties are yes/no flags for whether they
//                              gave a place, dates or pet types — never the
//                              place itself.
//   sitter_response_sent       A sitter successfully answered a care request
//                              with a price.
//   subscription_checkout_started  A sitter pressed Subscribe / Start free
//                              trial and was sent to Stripe. `plan` is the
//                              plan key. It does NOT mean they paid — Stripe
//                              is the source of truth for that.
//
//   lost_report_created        SERVER-CONFIRMED: an owner (or a linked sitter)
//                              turned on Lost Mode. `species`, `created_by`.
//   lost_report_shared         A share button on the "Share now" screen was
//                              used. `channel` native | whatsapp | facebook |
//                              copy | image | poster.
//   lost_report_resolved       SERVER-CONFIRMED: `outcome` found | closed.
//   tag_created                An owner created or replaced a collar QR tag.
//   tag_sheet_printed          The A4 sticker sheet was opened.
//
//   reference_link_copied      A sitter copied their personal reference link
//                              (part A). `with_template` yes/no.
//   reference_link_shared      A sitter sent the link through the phone's
//                              share sheet.
//   (reference_form_viewed / reference_submitted / reference_confirmed /
//   reference_owner_cta_clicked happen on the cookie-free /vouch pages and
//   are logged server-side by ExternalReferences::Events.)
//
//   lead_form_submitted        The holiday-checklist or sitter-guide form was
//                              sent (part D). `source` holiday_checklist |
//                              sitter_guide. Confirmation is server-side.
//   checklist_printed          Print / Save as PDF on the checklist sheet.
//   checklist_shared           "Share with my sitter" on the sheet.
//   guide_printed              Print / Save as PDF on the sitter guide.
//   emergency_plan_saved       An owner saved a pet's emergency plan (part E).
//   tag_nfc_link_copied        Lost Mode v1.2: the NFC link was copied.
//   tag_nfc_written            …written to a sticker through Web NFC (Android).
//   chip_reminder_shown        The chip registration card was shown.
//   chip_check_opened          "Check now" opened the registry link.
//   chip_check_confirmed       "I've checked it".
//   chip_marked_not_chipped    "{name} isn't chipped".
//   (Tag contact source qr|nfc is logged server-side: grep "tag_event".)
//
// PRIVACY RULE (do not break this)
// Event properties may contain step names, fixed keywords, counts and
// yes/no flags only. Never a name, email, address, city, pet name, free text
// or a price — those would turn cookie-free analytics into personal-data
// processing and cost us the "no cookie banner" position.

/** The complete set of events we send. A union rather than free-form strings
 *  so the same funnel step can never end up logged under two spellings. */
export type EventName =
  | 'sitter_wizard_step'
  | 'sitter_wizard_completed'
  | 'owner_wizard_step'
  | 'owner_wizard_completed'
  | 'home_search_submitted'
  | 'care_request_sent'
  | 'sitter_response_sent'
  | 'subscription_checkout_started'
  | 'registration_submitted'
  | 'subscription_started'
  | 'share_card_downloaded'
  | 'share_card_native_shared'
  | 'share_card_caption_copied'
  | 'lost_report_created'
  | 'lost_report_shared'
  | 'lost_report_resolved'
  | 'tag_created'
  | 'tag_sheet_printed'
  | 'reference_link_copied'
  | 'reference_link_shared'
  | 'lead_form_submitted'
  | 'checklist_printed'
  | 'checklist_shared'
  | 'guide_printed'
  | 'emergency_plan_saved'
  | 'tag_nfc_link_copied'
  | 'tag_nfc_written'
  | 'chip_reminder_shown'
  | 'chip_check_opened'
  | 'chip_check_confirmed'
  | 'chip_marked_not_chipped'

/** Umami accepts these value types. Keep them non-identifying — see the
 *  privacy rule above. */
export type EventProps = Record<string, string | number | boolean>

declare global {
  interface Window {
    umami?: { track?: (event: string, data?: EventProps) => void }
  }
}

/**
 * Send one event. Deliberately silent when Umami is not there: the script is
 * absent in development and in tests, and ad blockers remove it for a good
 * share of real visitors. Analytics must never be able to break a signup, so
 * every failure here is swallowed.
 */
export function track(event: EventName, data?: EventProps): void {
  try {
    if (typeof window === 'undefined') return
    window.umami?.track?.(event, data)
  } catch {
    // Blocked, offline, or a broken third-party script. Nothing to do.
  }
}

const sent = new Set<string>()

/**
 * Send an event at most once per page load.
 *
 * Needed because React runs in StrictMode (see entrypoints/inertia.tsx), which
 * mounts every component twice in development — a plain useEffect would count
 * each wizard step twice locally and once in production, so the two would never
 * agree. Deduplicating on a module-level set makes both behave the same.
 *
 * It also means stepping back and forward through the wizard counts a step
 * once, which is what a funnel wants: "how far did this person get", not "how
 * many times did they see this screen".
 */
export function trackOnce(event: EventName, data?: EventProps): void {
  const key = data ? `${event}:${JSON.stringify(data)}` : event
  if (sent.has(key)) return
  sent.add(key)
  track(event, data)
}

/** One server-confirmed event, as ApplicationController shares it. */
export type ServerEvent = { id: string; name: EventName; props?: EventProps }

const flushed = new Set<string>()

/**
 * Send the server-confirmed events for this page, each exactly once. The id
 * comes from the server, so React StrictMode's double effect and Inertia
 * re-renders with the same props cannot send an event twice.
 */
export function flushServerEvents(events: ServerEvent[] | undefined): void {
  for (const e of events ?? []) {
    if (!e?.id || flushed.has(e.id)) continue
    flushed.add(e.id)
    track(e.name, e.props)
  }
}
